import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "../../../common/element/Dialog";
import BtnPrimary from "../../../common/element/Buttons/btnPrimary";
import Select from "../../../common/element/Select";
import ThreeDotLoader from "../../../common/element/ButtonLoader/ThreeDotLoader";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";
import { addBatches, updateBatches } from "../../../redux/Batches/batchSlice";
import {
  selectAddBatchesLoading,
  selectAddBatchesData,
  selectAddBatchesError,
  selectUpdateBatchesLoading,
  selectUpdateBatchesData,
  selectUpdateBatchesError,
} from "../../../redux/Batches/batchSelector";
import { clearAddBatches, clearUpdateBatches } from "../../../redux/Batches/batchSlice";
import { getEmployees } from "../../../redux/Employee/employeesSlice";
import { selectEmployees } from "../../../redux/Employee/employeesSelector";

const AddEditBatch = ({
  dialogOpen,
  onDialogClose,
  batchData,
  isEdit = false,
  onSuccess,
  fetchBatches,
  companyId
}) => {
  const dispatch = useDispatch();

  // Redux selectors
  const addLoading = useSelector(selectAddBatchesLoading);
  const addData = useSelector(selectAddBatchesData);
  const addError = useSelector(selectAddBatchesError);
  const employees = useSelector(selectEmployees);
  const updateLoading = useSelector(selectUpdateBatchesLoading);
  const updateData = useSelector(selectUpdateBatchesData);
  const updateError = useSelector(selectUpdateBatchesError);

  // Helper to clear errors
  const clearBatchErrors = () => {
    dispatch(clearAddBatches());
    dispatch(clearUpdateBatches());
  };
  console.log(batchData, "batchDatabatchData")
  const [formData, setFormData] = useState({
    batchName: "",
    employees: [],
  });

  const [formError, setFormError] = useState({});

  const employeeOptions = employees.map(emp => ({
    value: emp.employee_id,
    label: emp.employee_name
  }));

  useEffect(() => {
    if (batchData && isEdit && employees.length) {
      setFormData({
        batchName: batchData.batch_name || "",
        employees: batchData.employees
          ?.map(emp =>
            employeeOptions.find(opt => opt.value === emp.employee_id)
          )
          .filter(Boolean) || [],
      });
    } else {
      setFormData({
        batchName: "",
        employees: [],
      });
    }
    setFormError({});
  }, [batchData, dialogOpen, isEdit, employees]);


  // Handle successful API response
  useEffect(() => {
    if (addData) {
      console.log("Batch added successfully:", addData);
      SweetAlertService.showSuccess({
        title: "Added",
        message: "Batch added successfully",
      });
      fetchBatches();
      dispatch(clearAddBatches());
      onSuccess?.();
      onDialogClose();
    }
  }, [addData, dispatch, onDialogClose, onSuccess]);

  // Cleanup errors on unmount
  useEffect(() => {
    return () => {
      clearBatchErrors();
    };
  }, []);

  useEffect(() => {
    if (updateData) {
      console.log("Batch updated successfully:", updateData);
      SweetAlertService.showSuccess({
        title: "Updated",
        message: "Batch updated successfully",
      });
      dispatch(clearUpdateBatches());
      fetchBatches()

      onSuccess?.();
      onDialogClose();
    }
  }, [updateData, dispatch, onDialogClose, onSuccess]);

  // ✅ Updated logic for fetching employees based on isEdit status
  useEffect(() => {
    if (dialogOpen) {
      const params = {
        status: isEdit ? 'all' : 'unassigned',
        company_id: companyId ? Number(companyId) : null
      };
      dispatch(getEmployees(params));
    }
  }, [dispatch, isEdit, dialogOpen, companyId]);

  // Handle API errors
  useEffect(() => {
    if (addError) {
      setFormError((prev) => ({ ...prev, api: addError }));
      SweetAlertService.showError({
        title: "Error",
        message: addError || "Failed to add batch",
      });
      // Clear error after showing popup so it can show again on next error
      dispatch(clearAddBatches());
    }
  }, [addError, dispatch]);

  useEffect(() => {
    if (updateError) {
      setFormError((prev) => ({ ...prev, api: updateError }));
      SweetAlertService.showError({
        title: "Error",
        message: updateError || "Failed to update batch",
      });
      // Clear error after showing popup so it can show again on next error
      dispatch(clearUpdateBatches());
    }
  }, [updateError, dispatch]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formError[field]) setFormError((prev) => ({ ...prev, [field]: "" }));
    if (formError.api) setFormError((prev) => ({ ...prev, api: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.batchName.trim())
      errors.batchName = "Name of batch is required";
    if (!formData.employees || formData.employees.length === 0)
      errors.employees = "Please select at least one employee";

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Prepare payload according to API schema
      const payload = {
        batch_name: formData.batchName,
        // batch_size: formData.employees.length, // Set batch_size to number of employees
        employee_ids: formData.employees.map(emp => emp.value),
        company_id: companyId ? Number(companyId) : null
      };

      if (isEdit && batchData?.batch_id) {
        // Update batch
        dispatch(updateBatches({ id: batchData.batch_id, data: { ...payload, company_id: payload.company_id } }));
      } else {
        // Add new batch
        dispatch(addBatches({ ...payload, company_id: payload.company_id }));
      }
    }
  };

  const buttonDisabled = addLoading || updateLoading;

  // Wrap dialog close to clear errors
  const handleDialogClose = () => {
    clearBatchErrors();
    onDialogClose();
  };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={handleDialogClose}
      width={640}
      onRequestClose={handleDialogClose}
      bodyOpenClassName="overflow-hidden"
    >
      {/* Header */}
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.661 17.9834H11.2402V18.8165H13.661V17.9834Z" fill="#344054" />
              <path d="M18.4294 15.754C17.9623 15.594 17.2591 15.2971 16.9508 15.0683V14.2949C18.0017 13.6934 18.7273 12.6395 18.9879 11.339C19.6596 11.1786 20.1315 10.5192 20.1315 9.92119C20.1315 9.36204 19.7356 8.96784 19.0934 8.8513V8.37575C19.0991 8.20089 19.1373 6.12937 17.8307 4.76908C17.1016 4.00991 16.1065 3.625 14.8734 3.625C13.6423 3.625 12.6537 4.00304 11.9277 4.74983C11.2028 4.00317 10.2171 3.625 8.98959 3.625C7.75254 3.625 6.75541 4.01083 6.02595 4.77178C4.72309 6.13079 4.76345 8.1966 4.76958 8.37587V8.8513C4.12737 8.96784 3.73151 9.36204 3.73151 9.92119C3.73151 10.5193 4.20364 11.179 4.87567 11.3391C5.13225 12.6085 5.8326 13.647 6.84155 14.2547V14.9364C6.5399 15.1986 5.92365 15.5629 5.54265 15.7644C2.07818 16.8961 2.00162 18.9931 2.00008 19.0821C2.00004 19.0845 2 19.087 2 19.0894V19.9572C2 20.1873 2.18652 20.3737 2.41653 20.3737H21.5835C21.8135 20.3737 22 20.1873 22 19.9572V19.0876C21.9996 18.9976 21.95 16.8713 18.4294 15.754ZM19.0934 9.70735C19.207 9.74671 19.2984 9.81214 19.2984 9.92119C19.2984 10.0524 19.2185 10.2204 19.0921 10.3488C19.0925 10.3226 19.0934 9.70735 19.0934 9.70735ZM4.56456 9.92119C4.56456 9.81214 4.65599 9.74671 4.76958 9.70735C4.76958 9.70735 4.77053 10.3226 4.77091 10.3488C4.64454 10.2204 4.56456 10.0524 4.56456 9.92119ZM18.2604 10.27C18.2604 10.474 18.2462 10.6847 18.2184 10.8952C18.047 12.164 17.3666 13.1754 16.3518 13.67C16.3501 13.6708 16.3485 13.6716 16.3468 13.6725C15.9083 13.8936 15.4126 14.0057 14.8734 14.0057C14.3343 14.0057 13.8386 13.8936 13.4001 13.6725C13.398 13.6714 13.3958 13.6704 13.3937 13.6694C13.0417 13.4993 12.7271 13.2611 12.4544 12.9614C12.763 12.4864 12.9849 11.9359 13.107 11.3393C13.7794 11.1795 14.2519 10.5196 14.2519 9.92123C14.2519 9.36209 13.8561 8.96789 13.2138 8.85134V8.50708C13.8466 8.37108 14.5314 8.15386 14.881 7.75729C15.4961 8.10401 16.7291 8.68023 18.2604 8.7722V10.27ZM16.1178 14.6582V15.099C16.0245 15.1951 15.8481 15.3489 15.5921 15.4525C15.4128 15.5257 15.2171 15.5628 15.0104 15.5628C14.7429 15.5628 14.4661 15.5017 14.1889 15.3817C13.944 15.2747 13.749 15.1445 13.629 15.0548V14.6582C14.02 14.7779 14.4362 14.8387 14.8734 14.8387C15.3106 14.8387 15.7268 14.7779 16.1178 14.6582ZM13.5092 15.9703L13.4464 16.1544C13.2948 16.0838 13.1366 16.0165 12.9709 15.9522C13.0587 15.9005 13.1442 15.8466 13.2241 15.7898C13.3049 15.8462 13.4006 15.9082 13.5092 15.9703ZM12.7959 14.2951V15.0705C12.611 15.214 12.3121 15.3663 12.0824 15.4835C12.0572 15.4963 12.0323 15.509 12.0079 15.5215C11.6234 15.2989 11.3164 15.0923 11.1376 14.9366V14.2551C11.4293 14.0799 11.6947 13.8685 11.9316 13.6228C12.191 13.8898 12.4802 14.1146 12.7959 14.2951ZM13.2125 10.3488C13.2128 10.3226 13.2138 9.70735 13.2138 9.70735C13.3274 9.74671 13.4188 9.81219 13.4188 9.92119C13.4188 10.0524 13.3388 10.2204 13.2125 10.3488ZM14.8734 4.45806C15.8691 4.45806 16.6608 4.75566 17.2265 5.34264C18.0014 6.14658 18.1975 7.31844 18.2459 7.93565C16.3571 7.80019 14.9546 6.82639 14.9402 6.81619C14.8131 6.72597 14.6463 6.71414 14.5079 6.78565C14.3694 6.85713 14.2824 6.99996 14.2824 7.15578C14.2824 7.2101 14.0723 7.44614 13.1755 7.6604C13.1107 7.06698 12.9311 6.20168 12.4459 5.41757C13.0194 4.78124 13.8343 4.45806 14.8734 4.45806ZM6.62733 5.34826C7.1936 4.75754 7.98842 4.45801 8.98963 4.45801C10.1328 4.45801 11.0053 4.85138 11.5828 5.62712C12.1701 6.41674 12.3242 7.41957 12.364 7.9354C10.4755 7.79932 9.07052 6.82606 9.05606 6.81585C8.92898 6.7258 8.7622 6.71418 8.62396 6.78565C8.48559 6.85717 8.3987 6.99992 8.3987 7.15566C8.3987 7.2158 8.13733 7.50896 6.92965 7.74067C6.44585 7.83351 5.9566 7.88833 5.61646 7.91882C5.66453 7.30044 5.8598 6.14891 6.62733 5.34826ZM5.64474 10.8964C5.61679 10.685 5.60263 10.4743 5.60263 10.27V8.75662C5.96118 8.72655 6.51695 8.66774 7.07881 8.56035C7.48467 8.4828 8.52274 8.28445 8.99334 7.75458C9.60776 8.10092 10.8451 8.6799 12.3807 8.77212V10.2699C12.3807 10.4739 12.3665 10.6847 12.339 10.8935C12.2409 11.5994 11.9827 12.2371 11.5926 12.7378C11.591 12.7398 11.5894 12.7418 11.5879 12.7439C11.2969 13.1318 10.9338 13.4372 10.505 13.6535C10.0527 13.8872 9.54286 14.0057 8.98959 14.0057C8.43627 14.0057 7.92644 13.8872 7.47147 13.6521C6.48196 13.1501 5.81598 12.1449 5.64474 10.8964ZM10.3046 14.6368V14.9475C10.1855 15.0653 9.95972 15.2629 9.66224 15.4037C9.21726 15.6149 8.7622 15.615 8.31656 15.4035C8.01991 15.2631 7.79382 15.0652 7.67461 14.9474V14.6366C8.08718 14.7707 8.5272 14.8388 8.98963 14.8388C9.45243 14.8388 9.89283 14.7706 10.3046 14.6368ZM2.83306 19.5407V19.0988C2.83601 19.0543 2.87092 18.6796 3.22097 18.1961C3.85122 17.3256 5.87729 16.5296 5.89816 16.5187C5.99963 16.4657 6.73742 16.0752 7.24317 15.6825C7.34843 15.774 7.47901 15.8758 7.63079 15.9731L7.21172 17.2304C7.18323 17.3158 7.18323 17.4082 7.21168 17.4936L7.89295 19.5407H2.83306ZM9.20831 19.5407H8.77095L8.0459 17.3622L8.39479 16.3156C8.78832 16.4199 9.1911 16.4199 9.58443 16.3156L9.93331 17.3622L9.20831 19.5407ZM15.1461 19.3664L15.088 19.5407H10.0863L10.7675 17.4936C10.796 17.4082 10.796 17.3158 10.7675 17.2303L10.3483 15.9731C10.4999 15.8759 10.6304 15.7743 10.7355 15.6829C11.0494 15.9266 11.46 16.1723 11.795 16.3585C11.9655 16.4566 12.0816 16.5168 12.0864 16.5194C12.1074 16.5302 12.1292 16.5393 12.1517 16.5465C12.6652 16.7101 13.1006 16.8934 13.4838 17.1077C15.0724 17.9883 15.1432 19.027 15.1461 19.0947V19.3664ZM15.6142 17.9678C15.359 17.5302 14.9255 17.0229 14.1891 16.5576L14.2788 16.2946C14.5233 16.3618 14.7681 16.3959 15.0104 16.3959C15.1709 16.3959 15.328 16.3807 15.4803 16.3509L15.817 17.3616L15.6142 17.9678ZM21.1669 19.5407H15.9792V19.5036L16.6511 17.4942C16.6797 17.4087 16.6798 17.3162 16.6514 17.2306L16.2575 16.0459C16.3808 15.9701 16.486 15.8906 16.5722 15.8158C17.1233 16.1776 17.9661 16.476 18.1644 16.5437C18.1674 16.5447 20.164 17.3187 20.7881 18.1896C21.1396 18.68 21.1653 19.0599 21.1669 19.0951V19.5407Z" fill="#344054" />
              <path d="M19.5399 17.9834H17.1191V18.8165H19.5399V17.9834Z" fill="#344054" />
            </svg>
          </span>
          <div>
            <h5 className="mb-0 text-gray-900 text-base font-semibold">
              {isEdit ? "Edit Batch" : "Add New Batch"}
            </h5>
          </div>
        </div>

        <button
          onClick={handleDialogClose}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      {/* Form Body */}
      <div className="px-6 ">
        {/* Batch Name */}
        <div className="flex mb-5">
          <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 pt-2">
            Name of batch*
          </label>
          <div className="w-2/3 pl-2">
            <input
              className="h-10 px-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={formData.batchName}
              placeholder="Enter Name of batch here"
              onChange={(e) => {
                const value = e.target.value.trimStart();
                const capitalized =
                  value.charAt(0).toUpperCase() + value.slice(1);
                handleInputChange("batchName", capitalized);
              }}
            />
            {formError.batchName && (
              <p className="text-xs text-red-600 mt-1">
                {formError.batchName}
              </p>
            )}
          </div>
        </div>

        {/* Add Employees */}
        <div className="flex mb-5">
          <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 pt-2">
            Add Employees*
          </label>
          <div className="w-2/3 pl-2">
            <Select
              placeholder="Select"
              options={employeeOptions}
              value={formData.employees}
              onChange={(opt) => handleInputChange("employees", opt)}
              isMulti
            />
            {formError.employees && (
              <p className="text-xs text-red-600 mt-1">
                {formError.employees}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex dailog-footer">
        <button
          className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold"
          onClick={handleDialogClose}
          style={{ backgroundColor: "#FFF4E5", borderColor: "#FFF4E5" }}
        >
          Cancel
        </button>
        <BtnPrimary
          disabled={buttonDisabled}
          onClick={handleSubmit}
          className="ml-1.5"
          style={{ backgroundColor: "#FF8E1C", borderColor: "#FF8E1C" }}
        >
          {buttonDisabled ? (
            <ThreeDotLoader />
          ) : isEdit ? (
            "Update Batch"
          ) : (
            "Add Batch"
          )}
        </BtnPrimary>
      </div>
    </Dialog>
  );
};

export default AddEditBatch;